<script setup async>
import {onMounted, onBeforeUnmount} from "vue";
import {useHeadService} from '~/composables/useHeadService';
import {useAuthService} from '~/composables/useAuthService';
import FrontendHeader from "~/components/layout/FrontendHeader.vue";
import PlayerCard from "~/components/players/PlayerCard.vue";
import {useValidation} from "~/composables/useValidation";
import {createError} from "nuxt/app";
import {useStatConverters} from "~/composables/useStatConverters";

definePageMeta({
  name: 'PlayerStats',
  path: '/players/:playerId/stats',
});

const route = useRoute();
const playerId = route.params.playerId;
// The profile gate first (PRIVACY_PLAN.md §4.1): the stats endpoint answers 403
// for a viewer who may not open the profile, which useCrudApi cannot tell from
// a 404, so the page asks the player page (a stub for them) before the numbers.
const {item: playerPage, getItem: getPlayerPage} = useCrudApi("api/v2/page/player");
await getPlayerPage(route.params.playerId);
const pagePrivate = computed(() => !!playerPage.value?.privateToViewer);
usePrivatePage(pagePrivate);
const {item: stats, getItem: getStats, hasItemErrors: hasStatsErrors} = useCrudApi("api/v2/realtimeStats");
if (!pagePrivate.value) {
  await getStats(route.params.playerId);
  if (hasStatsErrors.value) {
    throw createError({
      statusCode: 404, // or another relevant status code
      message: 'Sorry stats are not available for this player yet. Please check back later'
    });
  }
}

const { t } = useI18n()
const {setMeta} = useHeadService();
if (pagePrivate.value) {
  setMeta('Player stats on Tap Leagues', 'Player stats on Tap Leagues.');
} else {
  setMeta(
      stats.value?.player?.fullName + ' · Player Stats on Tap Leagues',
      stats?.value?.player?.fullName + ' is a player on Tap Leagues. View their stats here.'
  );
}
const {isAuthenticated, user, logout, isAdminOfClub} = useAuthService();
const router = useRouter();

const titleActions = computed(() => {
  const actions = [];

  if (user.value && user.value.playerId === playerId) {
    actions.push({
      title: 'Edit my profile',
      icon: 'mdi-pencil',
      route: {name: 'YourProfile'},
      color: 'info',
    });
  }

  actions.push({
    title: t("view_fixtures",0),
    icon: 'mdi-scoreboard',
    route: {
      name: 'FixtureSearch',
      query: {playerIds: [playerId]}
    },
    color: 'black',
  });

  return actions;
});
const {isAdminControlledPlayer} = useValidation();
const {outputDateFromDb} = useCrudConverters();
const {addListener, removeLocalListenersWithEvent} = useListenerService();
onMounted(() => {
  addListener("requestsUpdated", () => getStats(route.params.playerId));
});
onBeforeUnmount(() => {
  removeLocalListenersWithEvent("requestsUpdated");
});
const {doubleToPercentage,percentageToColor} =  useStatConverters();

// ── Form by period (Flutter _FormByPeriod) ──
const formPeriod = ref('3Months'); // default 3 months
const formChips = [
  {key: '1Month', label: '1 month'},
  {key: '3Months', label: '3 months'},
  {key: '6Months', label: '6 months'},
  {key: 'all', label: 'All time'},
];
const formData = computed(() => {
  const s = stats.value;
  const map = {
    '1Month': [s.wins1Month, s.losses1Month],
    '3Months': [s.wins3Months, s.losses3Months],
    '6Months': [s.wins6Months, s.losses6Months],
    'all': [s.wins, s.losses],
  };
  const [wins, losses] = map[formPeriod.value] || [0, 0];
  const played = (wins || 0) + (losses || 0);
  const winShare = played === 0 ? 0 : (wins / played);
  return {wins: wins || 0, losses: losses || 0, played, winPct: Math.round(winShare * 100)};
});

// Streak label (W3 / L2 / –) + best-streak suffix
const streakLabel = computed(() => {
  const s = stats.value;
  if (s.currentWinStreak > 0) return `W${s.currentWinStreak}`;
  if (s.currentLossStreak > 0) return `L${s.currentLossStreak}`;
  return '–';
});
const streakSuffix = computed(() => (stats.value.maxWinStreak > 0 ? ` · best W${stats.value.maxWinStreak}` : ''));

const squashLevelsLink = computed(() => {
  const id = stats.value?.player?.squashLevelId;
  return id ? `https://www.squashlevels.com/player_detail?player=${id}` : 'https://www.squashlevels.com';
});

</script>

<template>
  <base-page>
    <v-container v-if="pagePrivate" class="large">
      <private-notice reason="player" :name="playerPage?.player?.fullName === 'Private player' ? '' : playerPage?.player?.fullName" :follow-status="playerPage?.player?.followStatus" @follow="router.push('/players/' + playerId)"/>
    </v-container>
    <template v-else>
    <v-container>
      <frontend-header :user="stats.player" :actions="titleActions">
        <template #title>
          <player-card size="large" :player="stats.player"></player-card>
        </template>
        <template #subTitle>
          <v-tooltip
              text="This player is admin managed. You will need to contact the club admin to act on their behalf.">
            <template v-slot:activator="{ props }">
              <v-chip v-bind="props" v-if="isAdminControlledPlayer(stats.player)" class="mt-2" color="warning"
                      size="small">
                Admin managed
              </v-chip>
            </template>
          </v-tooltip>
        </template>
      </frontend-header>
    </v-container>

    <v-container v-if="stats.id == '00000000-0000-0000-0000-000000000000'">
      <v-alert type="info" variant="tonal" density="comfortable">
        Stats are still being calculated for this player. Please check back later.
      </v-alert>
    </v-container>

    <v-container v-else>
      <!-- ======== HEADLINE STAT STRIP (player-stats.html .statgrid) ======== -->
      <div class="tl-statgrid">
        <div class="tl-statcard">
          <div class="v">{{ stats.totalFixturesPlayed }}</div>
          <div class="k">Played</div>
        </div>
        <div class="tl-statcard">
          <div class="v">{{ stats.wins }} <small>W</small> · {{ stats.losses }} <small>L</small></div>
          <div class="k">Won · lost</div>
        </div>
        <div class="tl-statcard">
          <div class="v">{{ doubleToPercentage(stats.winPercentage) }}</div>
          <div class="k">Win rate</div>
        </div>
        <div class="tl-statcard">
          <div class="v">{{ streakLabel }}</div>
          <div class="k">Streak{{ streakSuffix }}</div>
        </div>
      </div>

      <!-- ======== TWO-COLUMN LAYOUT (web uses the extra width) ======== -->
      <v-row align="start" class="mt-2">

        <!-- col-main: Form + Opponents -->
        <v-col cols="12" md="8">

          <!-- Form by period (won / lost split bar) -->
          <div class="tl-section-head" style="margin-top:0;"><h2>Form</h2></div>
          <div class="form-chips">
            <button
                v-for="chip in formChips"
                :key="chip.key"
                type="button"
                class="form-chip"
                :class="{ active: formPeriod === chip.key }"
                @click="formPeriod = chip.key"
            >{{ chip.label }}</button>
          </div>
          <div class="tl-card form-card">
            <div class="form-bar">
              <template v-if="formData.played === 0">
                <div class="form-bar-empty"></div>
              </template>
              <template v-else>
                <div v-if="formData.winPct > 0" class="form-bar-win" :style="{ flexGrow: formData.winPct }"></div>
                <div v-if="formData.winPct < 100" class="form-bar-loss" :style="{ flexGrow: 100 - formData.winPct }"></div>
              </template>
            </div>
            <div class="form-counts">
              <span class="text-success">{{ formData.played === 0 ? '—' : `${formData.wins} won · ${formData.winPct}%` }}</span>
              <span class="form-counts-mid">{{ formData.played }} played</span>
              <span class="form-counts-loss">{{ formData.played === 0 ? '—' : `${formData.losses} lost · ${100 - formData.winPct}%` }}</span>
            </div>
          </div>

          <!-- Opponents (comp-rows) -->
          <div class="tl-section-head"><h2>Opponents</h2></div>
          <div class="tl-card" v-if="stats.mostFrequentOpponentPlayerId || stats.mostWinsOpponentPlayerId || stats.mostLossesOpponentPlayerId">
            <div class="opp-row" v-if="stats.mostFrequentOpponentPlayerId">
              <div class="opp-player"><player-card :id="stats.mostFrequentOpponentPlayerId"/></div>
              <div class="opp-meta">
                <div class="opp-tag">Most played against</div>
                <div class="opp-val">{{ stats.mostFrequentOpponentCount }} {{ t('fixtures', stats.mostFrequentOpponentCount) }}</div>
              </div>
            </div>
            <div class="opp-row" v-if="stats.mostWinsOpponentPlayerId">
              <div class="opp-player"><player-card :id="stats.mostWinsOpponentPlayerId"/></div>
              <div class="opp-meta">
                <div class="opp-tag">Most wins against</div>
                <span class="tl-chip tl-chip--ok">{{ stats.mostWinsOpponentCount }} W</span>
              </div>
            </div>
            <div class="opp-row" v-if="stats.mostLossesOpponentPlayerId">
              <div class="opp-player"><player-card :id="stats.mostLossesOpponentPlayerId"/></div>
              <div class="opp-meta">
                <div class="opp-tag">Most losses against</div>
                <span class="tl-chip tl-chip--warn">{{ stats.mostLossesOpponentCount }} L</span>
              </div>
            </div>
          </div>
          <div class="tl-card pa-4 text-medium-emphasis" v-else style="font-size:12.5px;">
            No opponent stats yet.
          </div>

        </v-col>

        <!-- col-side: Points + SquashLevels -->
        <v-col cols="12" md="4">
          <div class="stats-side-sticky">

            <div class="tl-section-head" style="margin-top:0;"><h2>Points</h2></div>
            <div class="points-grid">
              <!-- Same names as the club stats leaderboards (StatsView): the totals
                   count every match, the other two only wins / only losses. -->
              <div class="tl-statcard">
                <div class="v">{{ stats.totalPoints }}</div>
                <div class="k">Total points awarded</div>
              </div>
              <div class="tl-statcard">
                <div class="v">{{ stats.totalPointsConceded }}</div>
                <div class="k">Total points conceded</div>
              </div>
              <div class="tl-statcard">
                <div class="v">{{ stats.pointsAwarded }}</div>
                <div class="k">Points from wins</div>
              </div>
              <div class="tl-statcard">
                <div class="v">{{ stats.pointsConceded }}</div>
                <div class="k">Points conceded in losses</div>
              </div>
              <div class="tl-statcard points-grid-span">
                <div class="v">{{ stats.averageFixturePointsMargin >= 0 ? '+' : '' }}{{ stats.averageFixturePointsMargin }}</div>
                <div class="k">Average match points margin</div>
              </div>
            </div>

            <!-- SquashLevels -->
            <template v-if="stats.player.squashLevel > 0">
              <div class="tl-card sl-card">
                <div class="sl-mark"><v-icon size="22" color="textSubtle">mdi-chart-line</v-icon></div>
                <div class="sl-meta">
                  <div class="sl-value">{{ stats.player.squashLevel.toLocaleString() }}</div>
                  <div class="sl-label">SquashLevels</div>
                </div>
                <a class="sl-link" :href="squashLevelsLink" target="_blank" rel="noopener">View on SquashLevels ↗</a>
              </div>
            </template>

          </div>
        </v-col>

      </v-row>

      <!-- updated timestamp -->
      <div v-if="stats.updatedAt || stats.createdAt" class="stats-updated">
        Updated {{ outputDateFromDb(stats.updatedAt || stats.createdAt, 'd MMM yyyy') }}
      </div>

    </v-container>
    </template>
  </base-page>
</template>

<style scoped lang="scss">
/* ======================================================
   HEADLINE STAT STRIP
   mirrors mockups/redesign/player-stats.html .statgrid
   ====================================================== */
.tl-statgrid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

@media (min-width: 960px) {
  .tl-statgrid {
    grid-template-columns: repeat(4, 1fr);
  }
}

.tl-statcard {
  background: rgb(var(--v-theme-surface));
  border: 1px solid rgb(var(--v-theme-borderLight));
  border-radius: 14px;
  padding: 14px 16px;

  .v {
    font-size: 22px;
    font-weight: 800;
    letter-spacing: -0.5px;
    line-height: 1.3;

    small {
      font-size: 12px;
      font-weight: 600;
      color: rgb(var(--v-theme-textSubtle));
    }
  }

  .k {
    font-size: 10.5px;
    font-weight: 700;
    color: rgb(var(--v-theme-textSubtle));
    text-transform: uppercase;
    letter-spacing: 0.6px;
    margin-top: 2px;
  }
}

/* ======================================================
   SIDE COLUMN STICKY WRAPPER
   ====================================================== */
.stats-side-sticky {
  @media (min-width: 960px) {
    position: sticky;
    top: 88px; /* below topbar */
  }
}

/* ======================================================
   FORM — period chips + won/lost split bar
   ====================================================== */
.form-chips {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 12px;
}

.form-chip {
  padding: 8px 14px;
  border-radius: 10px;
  border: 1.5px solid rgb(var(--v-theme-borderLight));
  background: rgb(var(--v-theme-surface));
  font-size: 12px;
  font-weight: 700;
  color: rgb(var(--v-theme-text));

  &.active {
    background: rgb(var(--v-theme-brandTint));
    border-color: rgb(var(--v-theme-primary));
    color: rgb(var(--v-theme-primary));
  }
}

.form-card {
  padding: 16px;
}

.form-bar {
  display: flex;
  height: 10px;
  border-radius: 5px;
  overflow: hidden;
}

.form-bar-win {
  background: rgb(var(--v-theme-success));
}

.form-bar-loss {
  background: rgb(var(--v-theme-primary));
}

.form-bar-empty {
  flex: 1;
  background: rgb(var(--v-theme-borderLight));
}

.form-counts {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 8px;
  font-size: 11.5px;
  font-weight: 600;
}

.form-counts .text-success {
  color: rgb(var(--v-theme-success));
}

.form-counts-mid {
  color: rgb(var(--v-theme-charcoal));
}

.form-counts-loss {
  color: rgb(var(--v-theme-primary));
}

/* ======================================================
   OPPONENTS — comp rows
   ====================================================== */
.opp-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 11px 16px;

  & + .opp-row {
    border-top: 1px solid rgb(var(--v-theme-borderLight));
  }
}

.opp-player {
  flex: 1;
  min-width: 0;
}

.opp-meta {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 3px;
  text-align: right;
}

.opp-tag {
  font-size: 10.5px;
  font-weight: 600;
  color: rgb(var(--v-theme-textSubtle));
}

.opp-val {
  font-size: 12.5px;
  font-weight: 700;
}

/* status chip pill (global .tl-chip only sets colour) */
.tl-chip {
  display: inline-flex;
  align-items: center;
  padding: 3px 9px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
}

/* ======================================================
   POINTS — totals, then from wins | in losses, then avg margin full width
   ====================================================== */
.points-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.points-grid-span {
  grid-column: 1 / -1;
}

/* ======================================================
   SQUASHLEVELS card
   ====================================================== */
.sl-card {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 13px 14px;
  margin-top: 14px;
}

.sl-mark {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: rgb(var(--v-theme-bg, 250, 250, 250));
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.sl-meta {
  flex: 1;
  min-width: 0;
}

.sl-value {
  font-size: 13.5px;
  font-weight: 700;
}

.sl-label {
  font-size: 11px;
  color: rgb(var(--v-theme-textSubtle));
}

.sl-link {
  font-size: 12px;
  font-weight: 600;
  color: rgb(var(--v-theme-primary));
  text-decoration: none;
  flex-shrink: 0;
}

.stats-updated {
  margin-top: 14px;
  text-align: center;
  font-size: 11.5px;
  font-weight: 500;
  color: rgb(var(--v-theme-textSubtle));
}
</style>
